/** @typedef {"text" |"image" | "properties" | "facts" | "blockquote" | "list" | "links" | "map" } ContentType */

import { AttrsList, Table, Tgb, ObjectLink, ObjectLinkList } from "@/utils/dto/objects.js";
import { linkedObjectToPayload } from "@/utils/searchObjectsAndArticles.js";
import { v4 as uuidv4 } from "uuid";

/**
 * @typedef {Object} BlockContentRelation
 * @property {string} vistat_object - ID to related vistat object
 * @property {string} ids - IDs to related entity
 * @property {boolean} import_all
 */

class ContentBlockBase {
  /** @type {ContentType} */
  static type = undefined
  /** @type {BlockContentRelation|null} */
  import_settings = null
  imported = []
  imported_from = {}
  /** @type {Promise | undefined} */
  editorComponent
  is_deleted = false
  inited = false
  can_delete = true
  can_sorting = true
  /** @type Art */
  art = null

  static allow_import = false
  static allow_import_all = false
  static import_all_only = false
  static allow_create = false
  static human_name = ""

  /**
   * @type {Object}
   */
  data = {}

  constructor(payload = {}) {
    const data = JSON.parse(JSON.stringify(payload))

    if (data.hasOwnProperty('import_settings') && data.import_settings !== null || (data.type === "image_extension")) {
      this.import_settings = data.import_settings
      if (data.imported) this.imported = data.imported

      if (data.hasOwnProperty('imported_from') && data.imported_from !== null) {
        this.imported_from = { ...data.imported_from }
        delete data.imported_from
      }
    } else {
      delete data.imported
      this.data = this.initData(data)
    }

    if (data.hasOwnProperty('id')) {
      this.data.id = data.id
    }

    if (data.type === 'attrs') {
      this.is_summary = data.is_summary
    } else {
      this.is_summary = false
    }

    if (this.constructor.allow_import_all && this.constructor.import_all_only && !this.constructor.allow_create) {
      this.import_settings = Object.assign({}, this.import_settings, { import_all: true, ids: [], design_override: { by_id: {} } })
    }
  }

  /** @return Promise */
  loadRelationData() {
    throw new Error(`${this.constructor.type}: method loadRelationData need implement`);
  }

  /** @return {Object} */
  initData() {
    throw new Error(`${this.constructor.type}: method initData need implement`);
  }

  getRelatedDataOptions(id) {
    throw new Error(`${this.constructor.type}: method getRelatedDataOptions need implement`);
  }

  get humanReadableName() {
    return this.constructor.human_name
  }

  get allowCreate() {
    return this.constructor.allow_create
  }

  get allowImport() {
    return this.constructor.allow_import
  }

  get allowImportAll() {
    return this.constructor.allow_import_all
  }

  get importAllOnly() {
    return this.constructor.import_all_only
  }

  get isSaved() {
    return Boolean(this.data && this.data.id)
  }

  get type() {
    return this.import_settings === null ? 'edit' : 'import'
  }

  startEdit() {
    this.inited = true
  }

  startImport() {
    this.inited = true

    this.import_settings = {
      design_override: { by_id: {} },
      vistat_object: '',
      import_all: this.import_settings?.import_all ?? (this.allowImportAll ? this.importAllOnly : false),
      ids: []
    }
  }

  toSave() {
    if (this.is_deleted) {
      return {
        is_deleted: this.is_deleted,
        id: this.data.id,
        type: this.constructor.type,
      }
    }
    const data = Object({
      type: this.constructor.type,
    })

    if (this.data && this.data.hasOwnProperty('id') && this.data.id) {
      data.id = this.data.id
    }
    if (this.type === 'import') {
      data.import_settings = this.import_settings
    } else {
      if (this.data) {
        Object.keys(this.data).forEach(key => {
          data[key] = this.data[key]
        })
      }
    }


    return data
  }

  publicComponent = null
}

export class FreeCodeBlock extends ContentBlockBase {
  static human_name = "Код"
  static allow_create = true
  static type = "free_code"
  editorComponent = () => import("@/components/art/editor/FreeCodeBlockComponent.vue")
  publicComponent = () => import("@/components/art/public/FreeCodeBlockComponent.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData(data = {}) {
    return {
      descr: data.descr || "",
      code: data.code || ""
    }
  }
}

export class TextBlock extends ContentBlockBase {
  static human_name = "Текстовый блок"
  static allow_create = true
  static type = "text"
  editorComponent = () => import("@/components/art/editor/TextBlockComponent.vue")
  publicComponent = () => import("@/components/art/public/TextBlockComponent.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData(data = {}) {
    return {
      label: data.label || "",
      text: data.text || ""
    }
  }
}

export class VideoBlock extends ContentBlockBase {
  static human_name = "Видео"
  static allow_create = true
  static type = "video"
  editorComponent = () => import("@/components/art/editor/VideoBlockComponent.vue")
  publicComponent = () => import("@/components/art/public/VideoBlockComponent.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData(data = {}) {
    return {
      label: data.label || "",
      vk_video: data.vk_video || "",
      youtube: data.youtube || "",
      rutube: data.rutube || "",
    }
  }

  toSave() {
    const data = super.toSave();

    ;['vk_video', 'youtube', 'rutube'].forEach((key) => {
      if (data[key] === '') {
        data[key] = null
      }
    })

    return data
  }
}

export class PropertiesBlock extends ContentBlockBase {
  static human_name = 'Характеристики'
  static allow_import = true
  static allow_create = true
  static allow_import_all = true
  static import_all_only = true
  static type = "props"

  editorComponent = () => import("@/components/art/editor/PropertiesBlockComponent.vue")
  publicComponent = () => import("@/components/art/public/PropertiesBlockContent.vue")

  constructor(initialData) {
    super(initialData);
  }

  getRelatedDataOptions() {
    return []
  }

  loadRelationData() {
    return []
  }

  initData(initialData = {}) {
    const sourceProps = initialData.props || initialData
    const sourceProperties = sourceProps.items || []

    return {
      // type: initialData.type,
      props: {
        items: sourceProperties.map((prop, idx) => ({
          ...prop,
          id: String(prop?.id ?? prop?.uuid ?? `${idx}`),
          idx: prop?.idx ?? idx,
          uuid: prop.uuid || prop.id || `${idx}_${Date.now()}`,
          vistat_object: prop?.vistat_object ?? '',
          value_si: String(prop?.value_si ?? ''),
          value: prop?.value ?? null,
          date: prop?.date ?? {
            vfrom: prop?.date?.vfrom ?? null,
            vfrom_approx: Boolean(prop?.date?.vfrom_approx),
            vto: prop?.date?.vto ?? null,
            vto_approx: Boolean(prop?.date?.vto_approx),
          },
          template: prop?.template,
          template_id: String(prop?.template_id ?? prop?.template?.id ?? prop?.template ?? ''),
          template_label: String(prop?.template_label ?? prop?.template?.label ?? ''),
          template_ranging: String(prop?.template_ranging ?? prop?.template?.ranging ?? ''),
          template_value_type: String(prop?.template_value_type ?? prop?.template?.value?.type ?? ''),
          is_main: prop?.is_main,
          is_short: prop?.is_short,
          is_deleted: prop?.is_deleted,
        })),
        label: sourceProps.label || 'Характеристики',
        design: sourceProps.design || sourceProps.properties_design || null,
        design_theme: sourceProps.design_theme || sourceProps.properties_design_theme || null,
        design_extra: sourceProps.design_extra || sourceProps.properties_design_extra || null,
      }
    }
  }

  toSave() {
    const data = super.toSave()
    if (data.props && Object.prototype.hasOwnProperty.call(data.props, 'type')) {
      const props = { ...data.props }
      delete props.type
      data.props = props
    }
    return data
  }
}

export class MapBlock extends ContentBlockBase {
  static human_name = "Карта"
  static allow_import = true
  static allow_create = true
  static import_all_only = true
  static type = "map"
  editorComponent = () => import("@/components/art/editor/MapBlockComponent.vue")
  publicComponent = () => import("@/components/art/public/MapBlockComponent.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData({ map = { geodata: {} } }) {
    return {
      map: {
        geodata: map.geodata
      }
    }
  }

  startImport() {
    this.inited = true

    this.import_settings = {
      vistat_object: '',
      import_all: true,
      ids: []
    }
  }

  getRelatedDataOptions() {
    return []
  }

  loadRelationData() {
    return []
  }
}

export class TableBlock extends ContentBlockBase {
  static human_name = "Таблица"
  static allow_import = true
  static allow_create = true
  static allow_import_all = false
  static import_all_only = true
  static type = "table"
  editorComponent = () => import("@/components/art/editor/TableBlockComponent.vue")
  publicComponent = () => import("@/components/art/public/TableBlockComponent.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData(initialData) {
    return {
      table: new Table(initialData.table ?? { design: 'tables_default' }, false)
    }
  }

  toSave() {
    const data = super.toSave();

    if (typeof data.table === 'object') {
      data.table = new Table(data.table, true)
    }

    return data
  }

  startImport() {
    this.inited = true

    this.import_settings = {
      design_override: { by_id: {} },
      vistat_object: '',
      import_all: false,
      ids: []
    }
  }

  getRelatedDataOptions() {
    const options = [];
    this.imported.forEach((table) => {
      options.push(Object({ value: table.id, label: table.label || `График` }));
      // if (table.data.charts.length) {
      //   table.data.charts.forEach(chart => {
      //     options.push(Object({ value: chart.uuid, label: chart.chart_label || `График`, is_chart: true }));
      //   })
      // }
    })

    return options;
  }

  loadRelationData() {
    return this.art.fetchObject(this.import_settings.vistat_object)
      .then(obj => {
        this.imported = obj.tables ?? []
      })
  }
}

export class TgbBlock extends ContentBlockBase {
  static human_name = "ТГБ"
  static allow_import = true
  static allow_create = true
  static allow_import_all = true
  static import_all_only = true
  static type = "tgb"
  editorComponent = () => import("@/components/art/editor/TgbBlockComponent.vue")
  publicComponent = () => import("@/components/art/public/TgbBlockComponent.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData(initialData) {
    return {
      tgb: new Tgb(initialData.tgb ?? { design: 'tgbs_default' }, false)
    }
  }

  toSave() {
    const data = super.toSave();

    if (typeof data.tgb === 'object') {
      data.tgb = new Tgb(data.tgb, true)
    }

    return data
  }

  startImport() {
    this.inited = true

    this.import_settings = {
      design_override: {},
      vistat_object: '',
      import_all: false,
      ids: []
    }
  }

  getRelatedDataOptions() {
    const options = [];
    this.imported.forEach((tgb) => {
      options.push(Object({ value: tgb.id, label: tgb.label || `График` }));
    })

    return options;
  }

  loadRelationData() {
    return this.art.fetchObject(this.import_settings.vistat_object)
      .then(obj => {
        this.imported = obj.tgbs ?? []
      })
  }
}

export class FactBlock extends ContentBlockBase {
  static human_name = 'Факт'
  static allow_import = true
  static allow_import_all = true
  static import_all_only = true
  static type = "facts"

  editorComponent = () => null
  publicComponent = () => import("@/components/art/public/FactBlockContent.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData() {
    return Object()
  }

  getRelatedDataOptions() {
    return this.imported.map(fact => Object({
      value: fact.id,
      label: fact.description
    }))
  }

  loadRelationData() {
    return this.art.fetchObject(this.import_settings.vistat_object)
      .then(obj => {
        this.imported = obj.facts?.items ?? []
      })
  }
}

export class QuoteBlock extends ContentBlockBase {
  static human_name = 'Цитата'
  static allow_import = true
  static type = "quotes"
  editorComponent = () => null
  publicComponent = () => import("@/components/art/public/QuoteBlockContent.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData() {
    return Object()
  }

  getRelatedDataOptions() {
    return this.imported.map((quote, idx) => Object({
      value: quote.id,
      label: quote.text || `Цитата #${idx}`
    }))
  }

  loadRelationData() {
    return this.art.fetchObject(this.import_settings.vistat_object)
      .then(obj => {
        this.imported = obj.quotes?.items ?? []
      })
  }
}


export class ImageBlock extends ContentBlockBase {
  static human_name = 'Иллюстрация'
  static allow_import = true
  static allow_import_all = true
  static allow_create = true
  static type = "image"
  editorComponent = () => import("@/components/art/editor/ImageBlockComponent.vue")
  publicComponent = () => import("@/components/art/public/ImageBlockComponent.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData(data = null) {
    return typeof data === "object" && (data.image || data.icon) ? {
      ...(data.image ? { image: data.image } : {} ),
      ...(data.icon ? { icon: data.icon } : {} ),
      id: data.id
    } : null
  }

  getRelatedDataOptions() {
    return this.imported.map((image, id) => Object({
      value: image.id,
      label: image.label || `Изображение #${id + 1}`
    }))
  }

  loadRelationData() {
    return this.art.fetchObject(this.import_settings.vistat_object)
      .then(obj => {
        this.imported = obj.images ?? []
      })
  }

  toSave() {
    const data = super.toSave();

    if (typeof data.image === 'object') {
      /* любой сценарий кроме создания нового блока */
      delete data.image.image_450_file
      delete data.image.has_polygons
    }

    return data
  }
}

export class ImageExtentionBlock extends ContentBlockBase {
  static human_name = 'Связанные объекты'
  static allow_import = false
  static type = "image_extension"
  editorComponent = () => null
  publicComponent = () => import("@/components/art/public/RelatedLinkBlock.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData() {
    return Object()
  }
}


export class AttrBlock extends ContentBlockBase {
  static human_name = "Списки"
  static allow_import = true
  static allow_create = true
  static import_all_only = false
  static type = "attrs"
  editorComponent = () => import("@/components/art/editor/AttrBlockComponent.vue")
  publicComponent = () => import("@/components/art/public/AttrBlockContent.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData(initialData) {
    return {
      attrs: new AttrsList(initialData.attrs ?? {})
    }
  }

  toSave() {
    const data = super.toSave();

    if (typeof data.attrs === 'object') {
      data.attrs = new AttrsList(data.attrs, true)
      data.is_summary = this.is_summary ?? false
    }

    return data
  }

  getRelatedDataOptions() {
    return this.imported.map((image, id) => Object({
      value: image.id,
      label: image.label || `Список #${id + 1}`
    }))
  }

  loadRelationData() {
    return this.art.fetchObject(this.import_settings.vistat_object)
      .then(obj => {
        this.imported = obj.attrs_list ?? []
      })
  }

  static isSaved() {
    return Boolean(this.data && this.data.id)
  }
}

/** Базовый тип «Связи»: импорт из объекта. Создание — через LinkObjectsBlock / LinkEventsBlock. */
export class LinkBlock extends ContentBlockBase {
  static human_name = "Связи"
  static allow_import = true
  static allow_create = false
  static import_all_only = false
  static type = "links"
  editorComponent = () => import("@/components/art/editor/LinksBlockComponent.vue")
  publicComponent = () => import("@/components/art/public/LinksBlockComponent.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData(initialData) {
    const raw = initialData.links
    if (!raw) {
      if (typeof this.constructor.default_is_event_list === 'boolean') {
        const isEventList = this.constructor.default_is_event_list
        return {
          links: [
            new ObjectLinkList({
              label: isEventList ? "События" : "",
              is_event_list: isEventList,
              uuid: uuidv4(),
              items: [new ObjectLink({ uuid: uuidv4() })]
            }, false)
          ]
        }
      }
      return { links: [] }
    }
    const payload = Array.isArray(raw) ? (raw[0] ?? {}) : raw
    return {
      links: [new ObjectLinkList(payload, false)],
    }
  }

  toSave() {
    const data = super.toSave();

    if (this.type === 'edit') {
      data.links = new ObjectLinkList(data.links?.[0] ?? {}, true)
    } else {
      delete data.links
    }

    return data
  }

  getRelatedDataOptions() {
    return this.imported.map((image, id) => Object({
      value: image.id,
      label: image.label || `Связь #${id + 1}`
    }))
  }

  loadRelationData() {
    return this.art.fetchObject(this.import_settings.vistat_object)
      .then(obj => {
        this.imported = obj.links_list ?? []
      })
  }
}

export class LinkObjectsBlock extends LinkBlock {
  static human_name = "Связи - объекты"
  static allow_import = false
  static allow_create = true
  static create_key = "links_objects"
  static default_is_event_list = false
}

export class LinkArticlesBlock extends ContentBlockBase {
  static human_name = "Связи - статьи"
  static allow_import = false
  static allow_create = true
  static create_key = "links_articles"
  static type = "links_to_materials"
  editorComponent = () => import("@/components/art/editor/ArticleLinksBlockComponent.vue")
  publicComponent = () => import("@/components/art/public/ArticleLinksBlockComponent.vue")

  constructor(initialData) {
    super(initialData);
  }

  initData(initialData = {}) {
    const raw = initialData.links
    const payload = Array.isArray(raw) ? (raw[0] ?? {}) : raw
    const items = payload?.items || []

    return {
      links: {
        label: payload?.label || "",
        items: (items.length ? items : [{ uuid: uuidv4() }]).map(item => ({
          uuid: item.uuid || uuidv4(),
          target_material: item.target_material || item.target_object || null,
        }))
      },
    }
  }

  toSave() {
    const data = super.toSave()
    const items = data.links?.items || []

    data.links = {
      label: data.links?.label || "",
      items: items
        .filter(item => !item.is_deleted)
        .map(item => ({
          target_material: typeof item.target_material === 'object'
            ? item.target_material.id ?? item.target_material.value
            : item.target_material
        }))
        .filter(item => item.target_material)
    }

    return data
  }
}

export class LinkEventsBlock extends LinkBlock {
  static human_name = "Связи - события"
  static allow_import = false
  static allow_create = true
  static create_key = "links_events"
  static default_is_event_list = true
}
export class ArtEditStyles {
  constructor(data = {}) {
    this.font = {
      family: data?.font?.family ?? 'Arial',
      h1: data?.font?.h1 ?? 18,
      h2: data?.font?.h2 ?? 17,
      text: data?.font?.text ?? 15,
      menu: data?.font?.menu ?? 14,
    }
    this.background = {
      type: data?.background?.type ?? 'none',
      style: data?.background?.style ?? 'cover',
      opacity: data?.background?.opacity ?? 100,
      color: data?.background?.color ?? null,
      image: data?.background?.image ?? null,
      image_2: data?.background?.image_2 ?? null,
      size: data?.background?.size ?? 'attached',
    }
    this.menu = {
      position: data?.menu?.position ?? 'top',
      border_type: data?.menu?.border_type ?? 'solid',
      border_color: data?.menu?.border_color ?? null,
      border_radius: data?.menu?.border_radius ?? 0,
      border_position: data?.menu?.border_position ?? 'none',
      bg_type: data?.menu?.bg_type ?? 'none',
      bg_color: data?.menu?.bg_color ?? null,
      bg_image: data?.menu?.bg_image ?? null,
      bg_image_2: data?.menu?.bg_image_2 ?? null,
      separator: data?.menu?.separator ?? null,
      burger: data?.menu?.burger ?? 'always',
      burger_icon: data?.menu?.burger_icon ?? null,
      font_color: data?.menu?.font_color ?? null,
    }
    this.first = {
      position: data?.first?.position ?? 'left',
      width: data?.first?.width ?? 'none',
      h1_color: data?.first?.h1_color ?? null,
      h2_color: data?.first?.h2_color ?? null,
      text_bg_type: data?.first?.text_bg_type ?? 'none',
      text_bg_color: data?.first?.text_bg_color ?? null,
      text_bg_image: data?.first?.text_bg_image ?? null,
      text_bg_image_2: data?.first?.text_bg_image_2 ?? null,
      text_border_radius: data?.first?.text_border_radius ?? 0,
      form: data?.first?.form ?? 'oneline',
      icon_size: data?.first?.icon_size ?? 40,
      padding: data?.first?.padding ?? 0,
      show_image: data?.first?.show_image ?? 'show',
    }
  }
}

export class BlockEditStyles {
  constructor(data = {}) {
    this.id = data?.id ?? null
    this.type = data?.type ?? null
    this.styles = {
      border: {
        padding: data?.styles?.border?.padding ?? 0,
        border_radius: data?.styles?.border?.border_radius ?? 0,
        border_position: data?.styles?.border?.border_position ?? 'none',
        type: data?.styles?.border?.type ?? 'none',
        color: data?.styles?.border?.color ?? null,
        width: data?.styles?.border?.width ?? 2,
      },
      bg: {
        type: data?.styles?.bg?.type ?? 'none',
        color: data?.styles?.bg?.color ?? null,
        image: data?.styles?.bg?.image ?? null,
        image_2: data?.styles?.bg?.image_2 ?? null,
        size: data?.styles?.bg?.size ?? 'attached',
        blur: data?.styles?.bg?.blur ?? 0,
      },
      subtitle: {
        type: data?.styles?.subtitle?.type ?? 'none',
        bg_color: data?.styles?.subtitle?.bg_color ?? null,
        image: data?.styles?.subtitle?.image ?? null,
        image_2: data?.styles?.subtitle?.image_2 ?? null,
        title_color: data?.styles?.subtitle?.title_color ?? null,
        text_color: data?.styles?.subtitle?.text_color ?? null,
        padding: data?.styles?.subtitle?.padding ?? 0,
        border_radius: data?.styles?.subtitle?.border_radius ?? 0,
      },
      action: {
        text: data?.styles?.action?.text ?? null,
        url: data?.styles?.action?.url ?? null,
        text_color: data?.styles?.action?.text_color ?? null,
        bg_color: data?.styles?.action?.bg_color ?? null,
        position: data?.styles?.action?.position ?? 'center',
      },
      container: {
        width: data?.styles?.container?.width ?? 'none',
        position: data?.styles?.container?.position ?? 'center',
        cover: data?.styles?.container?.cover ?? false,
      },
    }
  }
}


export class Art {
  label = ''
  slug = ''
  sublabel = ''
  categories_ids = []
  categories_ids_by_user = []
  linked_object = null
  id = ""
  /** тип объектов для блока характеристик (как form.type у объектов); корень формы статьи → material_edit */
  type = null
  is_published = true
  is_comments_enabled = true
  is_spec_project = false
  spec_project_settings = {
    general_styles: null,
    blocks: [],
  }
  /** @type Array<ContentBlockBase>*/
  blocks = []
  objectsGetter = () => {
  }
  fetchObject = () => {
  }

  /**
   *
   * @param data
   * @param getter {Store}
   */
  constructor(data, getter, isSpecialProject) {
    const blocks = [
      FreeCodeBlock,
      TextBlock,
      VideoBlock,
      FactBlock,
      QuoteBlock,
      ImageBlock,
      MapBlock,
      TableBlock,
      AttrBlock,
      TgbBlock,
      LinkArticlesBlock,
      LinkBlock,
      PropertiesBlock,
      ImageExtentionBlock
    ]

    const newData = { ...data }
    if (!Number.isNaN(newData.date_score)) {
      const PERCENTAGE_SCORE = Number(newData.date_score * 100)
      newData.date_score = Number(PERCENTAGE_SCORE.toFixed(2));
      newData.date_score_ceil = Number(PERCENTAGE_SCORE.toFixed(0));
    }

    if (!Number.isNaN(newData.total_score)) {
      const PERCENTAGE_SCORE = Number(newData.total_score * 100)
      newData.total_score = Number(PERCENTAGE_SCORE.toFixed(2));
      newData.total_score_ceil = Number(PERCENTAGE_SCORE.toFixed(0));
    }

    if (!Number.isNaN(newData.scoring_score)) {
      const PERCENTAGE_SCORE = Number(newData.scoring_score * 100)
      newData.scoring_score = Number(PERCENTAGE_SCORE.toFixed(2));
      newData.scoring_score_ceil = Number(PERCENTAGE_SCORE.toFixed(0));
    }


    this.objectsGetter = getter.getters['objects/cachedObjectsGetter']
    this.fetchObject = (id) => getter.dispatch('objects/publicObject', id)

    Object.keys(newData).forEach(key => {
      if (key === "blocks") {
        this.blocks = newData.blocks.map(block => {
          const isArticleLinksBlock = block.type === LinkBlock.type && (
            Array.isArray(block.links)
              ? block.links.some(list => list?.is_article_list)
              : block.links?.is_article_list
          )
          const BlockClass = isArticleLinksBlock
            ? LinkArticlesBlock
            : blocks.find(item => item.type === block.type)
          const newBlock = new BlockClass(block)
          newBlock.art = this
          newBlock.startEdit()

          return newBlock
        })
      } else {
        this[key] = newData[key]
      }
    })

    // Object.assign(this, newData)

    if (isSpecialProject) {
      this.initializeBlockStyles()
    }
  }

  initializeBlockStyles() {
    if (!this.spec_project_settings.blocks) {
      this.spec_project_settings.blocks = []
    }

    this.blocks.forEach((block, index) => {
      if (block.data && block.data.id) {
        const existingStyles = this.spec_project_settings.blocks.find(style => style.id === block.data.id)

        if (!existingStyles) {
          const defaultStyles = new BlockEditStyles({
            id: block.data.id,
            type: block.constructor.type
          })

          this.spec_project_settings.blocks.push(defaultStyles)
        }
      } else if (block.imported) {
        block.imported.forEach(el => {
          const existingStyles = this.spec_project_settings.blocks.find(style => style.id === el.id)

          if (!existingStyles) {
            const defaultStyles = new BlockEditStyles({
              id: el.id,
              type: block.constructor.type
            })

            this.spec_project_settings.blocks.push(defaultStyles)
          }
        })
      }
    })
  }

  async init() {
    return Promise.all(
      this.blocks.filter(x => x.import_settings).map(x => x.loadRelationData())
    )
  }

  /**
   * Получить стили блока по его ID
   * @param {string} blockId - ID блока
   * @returns {BlockEditStyles|null}
   */
  getBlockStyles(blockId) {
    if (!this.spec_project_settings?.blocks || !Array.isArray(this.spec_project_settings.blocks)) {
      return null
    }
    const blockStyle = this.spec_project_settings.blocks.find(block => block.id === blockId)
    return blockStyle ? new BlockEditStyles(blockStyle) : null
  }

  /**
   * Установить стили для блока
   * @param {string} blockId - ID блока
   * @param {string} blockType - Тип блока
   * @param {Object} styles - Стили блока
   */
  setBlockStyles(blockId, blockType, styles) {
    // Инициализируем массив blocks если его нет
    if (!this.spec_project_settings.blocks) {
      this.spec_project_settings.blocks = []
    }

    const existingIndex = this.spec_project_settings.blocks.findIndex(block => block.id === blockId)
    const blockStyles = new BlockEditStyles({
      id: blockId,
      type: blockType,
      styles: {
        ...styles.styles,
        ...styles
      }
    })

    if (existingIndex !== -1) {
      this.spec_project_settings.blocks[existingIndex] = blockStyles
    } else {
      this.spec_project_settings.blocks.push(blockStyles)
    }
  }

  /**
   * Удалить стили блока
   * @param {string} blockId - ID блока
   */
  removeBlockStyles(blockId) {
    if (!this.spec_project_settings?.blocks || !Array.isArray(this.spec_project_settings?.blocks)) {
      return
    }
    const index = this.spec_project_settings.blocks.findIndex(block => block.id === blockId)
    if (index !== -1) {
      this.spec_project_settings.blocks.splice(index, 1)
    }
  }

  /**
   * Обновить стили блоков при изменении порядка
   */
  updateBlockStylesOrder() {
    if (!this.spec_project_settings?.blocks || !Array.isArray(this.spec_project_settings?.blocks)) {
      return
    }

    this.blocks.forEach((block, index) => {
      if (block.data && block.data.id) {
        const existingStyles = this.getBlockStyles(block.data.id)
        if (existingStyles) {
          existingStyles.order = index
          this.setBlockStyles(block.data.id, block.constructor.type, existingStyles)
        }
      }
    })
  }

  toSave(extraDeletedBlocks = []) {
    if (this.spec_project_settings?.general_styles) {
      this.spec_project_settings.general_styles = new ArtEditStyles(this.spec_project_settings.general_styles);
    } else if (this.spec_project_settings && !this.spec_project_settings?.general_styles) {
      this.spec_project_settings.general_styles = new ArtEditStyles();
    }

    // Обновляем стили блоков перед сохранением
    this.updateBlockStylesOrder()

    return {
      label: this.label,
      slug: this.slug,
      sublabel: this.sublabel,
      categories_ids: Array.isArray(this.categories_ids) ? this.categories_ids : [],
      categories_ids_by_user: Array.isArray(this.categories_ids_by_user) ? this.categories_ids_by_user : [],
      linked_object: linkedObjectToPayload(this.linked_object),
      is_published: this.is_published,
      is_comments_enabled: this.is_comments_enabled,
      is_spec_project: this.is_spec_project,
      spec_project_settings: this.spec_project_settings,
      id: this.id,
      ...(this.type && (this.type.id || this.type.key) ? { type: this.type } : {}),
      blocks: [...this.blocks.map(x => x.toSave()), ...extraDeletedBlocks]
    }
  }
}
